type ActionErrorReason =
  | "ALREADY_SUBMITTED"
  | "ALREADY_VOTED"
  | "BAD_MESSAGE_SHAPE"
  | "CLUE_LEAKS_TOPIC"
  | "CLUE_TOO_LONG"
  | "CLUE_TOO_MANY_WORDS"
  | "CANNOT_KICK_IMPOSTER"
  | "CANNOT_KICK_SELF"
  | "HOST_REQUIRED"
  | "INVALID_PHASE"
  | "INVALID_SESSION"
  | "INVALID_SUSPECT"
  | "NAME_RESERVED"
  | "NAME_TAKEN"
  | "NAME_TOO_LONG"
  | "NO_SESSION"
  | "NOT_HOST"
  | "ROOM_FULL"
  | "ROOM_IN_PROGRESS"
  | "ROOM_NOT_FOUND"
  | "SELF_VOTE_NOT_ALLOWED"
  | "SPECTATOR_CANNOT_ACT"
  | "SPECTATOR_FORBIDDEN"
  | "STATE_TOO_LARGE"
  | "TARGET_NOT_SPECTATOR"
  | "WRONG_PHASE"

type TerminalReason = "BAD_TOKEN" | "HOST_KICKED" | "ROOM_EXPIRED" | "UNKNOWN_PLAYER"

export const GENERIC_ACTION_ERROR = "That action could not be completed. Try again."

/*
Scenario mapping:
- Inline validation: player names, room codes, clue text before submit.
- Dialog: ROOM_IN_PROGRESS offers spectator entry instead of replacing the join form.
- Toast: transient ACTION_REJECTED failures during active play.
- Reconnect overlay: temporary socket disconnect/reconnect status.
- Full-screen terminal: host removal, stale saved session, expired room, app crash boundary.
*/
export const ACTION_ERROR_MESSAGES: Record<ActionErrorReason, string> = {
  ALREADY_SUBMITTED: "You already submitted for this step.",
  ALREADY_VOTED: "Your vote is already locked in.",
  BAD_MESSAGE_SHAPE: "That action could not be read. Refresh and try again.",
  CLUE_LEAKS_TOPIC: "Choose a clue that does not repeat the topic.",
  CLUE_TOO_LONG: "Use a shorter clue.",
  CLUE_TOO_MANY_WORDS: "Use one word for your clue.",
  CANNOT_KICK_IMPOSTER: "That player cannot be removed right now.",
  CANNOT_KICK_SELF: "That player cannot be removed right now.",
  HOST_REQUIRED: "Only the host can do that.",
  INVALID_PHASE: "That step is no longer active.",
  INVALID_SESSION: "Your room session ended. Rejoin to continue.",
  INVALID_SUSPECT: "Choose a player who is still in this round.",
  NAME_RESERVED: "That name is reserved.",
  NAME_TAKEN: "That name is already in this room.",
  NAME_TOO_LONG: "Use 20 characters or fewer.",
  NO_SESSION: "Rejoin the room to continue.",
  NOT_HOST: "Only the host can do that.",
  ROOM_FULL: "This room is full.",
  ROOM_IN_PROGRESS: "Game in progress. Join as a spectator?",
  ROOM_NOT_FOUND: "That room could not be found.",
  SELF_VOTE_NOT_ALLOWED: "Choose another player.",
  SPECTATOR_CANNOT_ACT: "Spectators can watch, but cannot do that.",
  SPECTATOR_FORBIDDEN: "Join as a player to do that.",
  STATE_TOO_LARGE: "The room could not save that action. Try again.",
  TARGET_NOT_SPECTATOR: "Choose a spectator to promote.",
  WRONG_PHASE: "That step is no longer active.",
}

export const TERMINAL_MESSAGES: Record<TerminalReason, { title: string; description: string }> = {
  BAD_TOKEN: {
    title: "Session ended",
    description: "Rejoin the room to continue.",
  },
  HOST_KICKED: {
    title: "You left this room",
    description: "Return home to join another room.",
  },
  ROOM_EXPIRED: {
    title: "Room expired",
    description: "This room is no longer available.",
  },
  UNKNOWN_PLAYER: {
    title: "Session ended",
    description: "Rejoin the room to continue.",
  },
}

export function formatActionError(reason?: string) {
  return ACTION_ERROR_MESSAGES[reason as ActionErrorReason] ?? GENERIC_ACTION_ERROR
}

export function formatNameError(reason?: string) {
  if (reason === "NAME_TOO_LONG" || reason === "NAME_RESERVED" || reason === "NAME_TAKEN") {
    return formatActionError(reason)
  }

  if (reason === "PROFANITY") {
    return "Choose a different name."
  }

  return "Use letters, numbers, spaces, hyphens, underscores, or apostrophes."
}

export function formatTerminalReason(reason?: string) {
  return (
    TERMINAL_MESSAGES[reason as TerminalReason] ?? {
      title: "Session ended",
      description: "Return home to continue.",
    }
  )
}
